// Hook pour gérer les Push Notifications - Version simplifiée
import { useState, useEffect, useCallback } from 'react';
import { 
  requestNotificationPermission, 
  onForegroundMessage, 
  showLocalNotification,
  initializeFirebase,
  checkMessagingSupport
} from '../lib/firebase';
import { api } from '../services/api';
import { toast } from 'sonner';

export const usePushNotifications = (customerId = null) => {
  const [permission, setPermission] = useState('default');
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true); // Assume supported initially

  // Check if notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      // Basic support check
      const basicSupport = typeof window !== 'undefined' && 
                          'Notification' in window && 
                          'serviceWorker' in navigator;
      
      setSupported(basicSupport);
      
      if (basicSupport) {
        setPermission(Notification.permission);
        
        // Initialize Firebase (non-blocking)
        try {
          initializeFirebase();
        } catch (e) {
          console.log('Firebase init error (non-fatal):', e);
        }
      }
    };
    
    checkSupport();
  }, []);

  // Subscribe to foreground messages
  useEffect(() => {
    if (permission !== 'granted') return;

    try {
      onForegroundMessage((payload) => {
        console.log('Notification foreground:', payload);
        
        const title = payload.notification?.title || payload.data?.title || 'La Petite Épicerie';
        const body = payload.notification?.body || payload.data?.body || '';
        const type = payload.data?.type || 'general';

        // Show toast notification
        toast.success(title, {
          description: body,
          duration: 8000
        });

        // Also show native notification if app is not focused
        if (document.hidden) {
          showLocalNotification(title, body, '/logo-192.png', payload.data);
        }
      });
    } catch (error) {
      console.log('Foreground message setup error (non-fatal):', error);
    }
  }, [permission]);

  // Request permission and register token
  const requestPermission = useCallback(async () => {
    if (!supported) {
      toast.error('Les notifications ne sont pas supportées sur ce navigateur');
      return false;
    }

    setLoading(true);

    try {
      const result = await requestNotificationPermission();
      
      if (result) {
        // Could be a real FCM token or 'basic-notifications-only'
        const isBasicOnly = result === 'basic-notifications-only';
        
        setToken(result);
        setPermission('granted');

        // Save token to backend if it's a real FCM token and customer is logged in
        if (!isBasicOnly && customerId) {
          try {
            await api.savePushToken(customerId, result);
            console.log('Token FCM enregistré pour le client:', customerId);
          } catch (error) {
            console.error('Erreur enregistrement token:', error);
          }
        }

        toast.success('🔔 Notifications activées !', {
          description: isBasicOnly 
            ? 'Notifications basiques activées' 
            : 'Vous recevrez des alertes pour les messages et promos'
        });
        return true;
      } else {
        setPermission(Notification.permission);
        if (Notification.permission === 'denied') {
          toast.error('Notifications bloquées', {
            description: 'Autorisez les notifications dans les paramètres de votre navigateur'
          });
        }
        return false;
      }
    } catch (error) {
      console.error('Erreur notifications:', error);
      toast.error('Erreur lors de l\'activation des notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported, customerId]);

  return {
    permission,
    token,
    loading,
    supported,
    isEnabled: permission === 'granted',
    isDenied: permission === 'denied',
    requestPermission
  };
};

export default usePushNotifications;
